import { forwardRef, useCallback, useEffect, useState } from "react";
import { X, RefreshCw, Loader, Check, ChevronsDownUp } from "lucide-react";

import { cn, debounce } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

export type ComboboxOption<T = unknown> = {
  value: string | number;
  label: string;
  raw?: T;
};

export interface ComboboxStaticProps {
  buttonClassName?: string;
  boxWrapperClassName?: string;
  boxInputClassName?: string;
  boxListClassName?: string;
  boxGroupClassName?: string;
  boxItemClassName?: string;
  options: ComboboxOption[];
  value?: string;
  buttonPlaceholder?: string;
  inputPlaceholder?: string;
  onChange?: (option: ComboboxOption | null) => void;
  onReload?: () => void;
  onLoading?: boolean;
}

const ComboboxStatic = forwardRef<HTMLDivElement, ComboboxStaticProps>(
  (
    {
      buttonClassName,
      boxWrapperClassName,
      boxInputClassName,
      boxListClassName,
      boxGroupClassName,
      boxItemClassName,
      options,
      value,
      onChange,
      onReload,
      onLoading,
      buttonPlaceholder,
      inputPlaceholder,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [filteredOptions, setFilteredOptions] =
      useState<ComboboxOption[]>(options);

    useEffect(() => {
      setFilteredOptions(options);
    }, [options]);

    const onSearchChange = (value: string) => {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOptions(filtered);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearchChange = useCallback(
      debounce((value: string) => {
        onSearchChange(value);
      }, 1000),
      [onSearchChange]
    );

    const onHandleChangeSearchInput = (value: string) => {
      setSearch(value);
      debouncedSearchChange(value);
    };

    const clearInputAndSelection = () => {
      if (hasThingsToClear()) {
        setSearch("");
        setFilteredOptions(options);
      }
    };

    const hasThingsToClear = (): boolean => {
      return !!value || !!search;
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            ref={ref}
            role="combobox"
            className={cn(
              // Base styles matching input field from Figma
              "aero:flex aero:w-full aero:h-10 aero:items-center aero:justify-between aero:cursor-pointer",
              "aero:px-4 aero:py-3 aero:gap-2",
              "aero:bg-white aero:border aero:border-neutral-300 aero:rounded",
              "aero:shadow-none",
              "aero:font-inter aero:font-normal aero:text-sm aero:leading-5",
              // Focus states
              "aero:focus-visible:outline-none aero:focus-visible:border-ring aero:focus-visible:ring-ring/50 aero:focus-visible:ring-[3px]",
              "aero:data-[state=open]:border-ring aero:data-[state=open]:ring-ring/50 aero:data-[state=open]:ring-[3px]",
              // Text color
              value ? "aero:text-foreground" : "aero:text-neutral-500",
              buttonClassName
            )}
          >
            <span className="aero:text-sm aero:truncate aero:max-w-max">
              {value
                ? options.find((option) => option.value === value)?.label
                : buttonPlaceholder
                  ? buttonPlaceholder
                  : "Select Option"}
            </span>
            <div className="aero:flex aero:items-center aero:gap-2">
              {onReload && (
                <Button
                  // design="text"
                  type="button"
                  size="iconMd"
                  className={cn(
                    "aero:!rounded-full aero:my-1 aero:h-auto aero:w-auto aero:p-1",
                    onLoading ? "aero:bg-orange-400 aero:text-orange-800" : ""
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    onReload();
                  }}
                >
                  <RefreshCw
                    className={cn(
                      onLoading ? "aero:animate aero:animate-spin" : ""
                    )}
                    size={10}
                  />
                </Button>
              )}
              <ChevronsDownUp className="aero:h-4 aero:w-4 aero:shrink-0 aero:opacity-50" />
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent className="aero:w-auto aero:p-0" align="start">
          <Command className={boxWrapperClassName}>
            <div className="aero:relative">
              <CommandInput
                placeholder={
                  inputPlaceholder ? inputPlaceholder : "Search item..."
                }
                className={cn("aero:h-9", boxInputClassName)}
                value={search}
                onValueChange={onHandleChangeSearchInput}
              />
              {hasThingsToClear() && (
                <Button
                  design="text"
                  size="iconMd"
                  className="aero:absolute aero:right-2 aero:top-1/2 aero:-translate-y-1/2 aero:!rounded-full aero:h-auto aero:w-auto aero:p-1"
                  onClick={() => {
                    clearInputAndSelection();
                    if (onChange) {
                      onChange(null);
                    }
                  }}
                >
                  <X size={10} />
                </Button>
              )}
            </div>
            <CommandList
              className={cn("aero:overflow-y-hidden", boxListClassName)}
            >
              <ScrollArea className="aero:h-72">
                {onLoading ? (
                  <CommandEmpty className="aero:flex aero:flex-col aero:h-1/2 aero:justify-center aero:gap-2 aero:items-center aero:text-muted-foreground">
                    <Loader
                      size={20}
                      className="aero:animate aero:animate-spin"
                    />
                    <span className="aero:text-sm aero:font-semibold">
                      Load data.
                    </span>
                  </CommandEmpty>
                ) : !filteredOptions.length ? (
                  <CommandEmpty>No item found.</CommandEmpty>
                ) : (
                  <CommandGroup className={boxGroupClassName} forceMount={true}>
                    {filteredOptions.map((option) => (
                      <CommandItem
                        className={boxItemClassName}
                        value={option.value.toString()}
                        key={option.value}
                        onSelect={() => {
                          if (onChange) {
                            onChange(option);
                          }
                          setOpen(false);
                        }}
                      >
                        {option.label}
                        <Check
                          className={cn(
                            "aero:ml-auto aero:h-4 aero:w-4",
                            option.value === value
                              ? "aero:opacity-100"
                              : "aero:opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

export { ComboboxStatic };
